import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  PaperPlaneTilt,
  Calendar,
  Clock,
  Users,
  Eye,
  Check,
  X,
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
  Envelope,
  CheckCircle,
  XCircle,
  WarningCircle,
  Hourglass,
  PaperPlaneRight,
  ArrowsClockwise,
  Lightning,
  ChartBar,
  ListChecks,
  ClockCountdown,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  bg: "#F5E6E0",
  green: "#2D5A3D",
  greenDark: "#1E3D2A",
  orange: "#E07A3D",
  tan: "#D4A574",
  text: "#1A1A1A",
  textMuted: "#6B5B4F",
  white: "#FFFFFF",
  red: "#E74C3C",
  yellow: "#F59E0B",
  blue: "#3B82F6",
  border: "#E6DED3",
};

const DAYS_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const MONTHS_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const STATUS_META = {
  draft: { label: "Entwurf", color: COLORS.yellow, icon: Hourglass, bg: "#FEF3C7" },
  approved: { label: "Freigegeben", color: COLORS.green, icon: CheckCircle, bg: "#D1FAE5" },
  rejected: { label: "Abgelehnt", color: COLORS.red, icon: XCircle, bg: "#FEE2E2" },
  sent: { label: "Versendet", color: COLORS.blue, icon: PaperPlaneRight, bg: "#DBEAFE" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${DAYS_DE[d.getDay()]}, ${d.getDate()}.${String(d.getMonth() + 1).padStart(2, "0")}. · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return `${MONTHS_DE[m - 1]} ${y}`;
}

// ═══════════════════════════════════════════════════════════
// SUB-TAB NAV
// ═══════════════════════════════════════════════════════════
const SUB_TABS = [
  { id: "overview", label: "Übersicht", icon: ChartBar },
  { id: "month", label: "Monats-Freigabe", icon: ListChecks },
  { id: "history", label: "Historie", icon: ClockCountdown },
  { id: "automations", label: "Automationen", icon: Lightning },
];

function SubTabBar({ active, onChange }) {
  return (
    <div
      className="flex gap-1 p-1 rounded-2xl mb-5 overflow-x-auto"
      style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}
    >
      {SUB_TABS.map((t) => {
        const Icon = t.icon;
        const isActive = active === t.id;
        return (
          <motion.button
            key={t.id}
            whileTap={{ scale: 0.97 }}
            onClick={() => onChange(t.id)}
            className="flex-1 py-2.5 px-3 rounded-xl flex items-center justify-center gap-2 text-sm font-bold min-w-fit whitespace-nowrap"
            style={{
              backgroundColor: isActive ? COLORS.green : "transparent",
              color: isActive ? COLORS.white : COLORS.textMuted,
            }}
          >
            <Icon size={16} weight={isActive ? "fill" : "regular"} />
            <span>{t.label}</span>
          </motion.button>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// STATUS BADGE
// ═══════════════════════════════════════════════════════════
function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.draft;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
      style={{ backgroundColor: meta.bg, color: meta.color }}
    >
      <Icon size={12} weight="fill" />
      {meta.label}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════
// OVERVIEW TAB
// ═══════════════════════════════════════════════════════════
function OverviewPanel({ token, onJumpToMonth }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/newsletter/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error("Übersicht konnte nicht geladen werden");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-12">
        <span className="text-4xl animate-bounce">☕</span>
        <p className="mt-2 text-sm" style={{ color: COLORS.textMuted }}>Laden…</p>
      </div>
    );
  }
  if (!data) return null;

  const stats = [
    { label: "Empfänger gesamt", value: data.total_recipients, icon: Users, color: COLORS.green },
    { label: "Zu prüfen", value: data.pending_count, icon: Hourglass, color: COLORS.yellow },
    { label: "Freigegeben", value: data.approved_count, icon: CheckCircle, color: COLORS.green },
    { label: "Versendet (Monat)", value: data.sent_this_month, icon: PaperPlaneRight, color: COLORS.blue },
  ];

  return (
    <div className="space-y-5">
      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.label}
              className="p-4 rounded-2xl"
              style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon size={20} weight="fill" style={{ color: s.color }} />
              </div>
              <div className="text-2xl font-black" style={{ color: COLORS.text }}>{s.value}</div>
              <div className="text-xs mt-1" style={{ color: COLORS.textMuted }}>{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Next campaign */}
      <div
        className="p-5 rounded-2xl"
        style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black text-sm flex items-center gap-2" style={{ color: COLORS.text }}>
            <Clock size={18} weight="fill" style={{ color: COLORS.green }} />
            Nächster geplanter Versand
          </h3>
          <button
            onClick={() => onJumpToMonth(data.current_month)}
            className="text-xs font-bold underline"
            style={{ color: COLORS.green }}
          >
            Alle anzeigen →
          </button>
        </div>
        {data.next_campaign ? (
          <div className="flex items-start gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
              style={{ backgroundColor: data.next_campaign.color + "20" }}
            >
              {data.next_campaign.emoji}
            </div>
            <div className="flex-1">
              <div className="font-bold" style={{ color: COLORS.text }}>
                {data.next_campaign.subject}
              </div>
              <div className="text-sm mt-1" style={{ color: COLORS.textMuted }}>
                {fmtDate(data.next_campaign.scheduled_for)}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <StatusBadge status={data.next_campaign.status} />
                <span className="text-xs" style={{ color: COLORS.textMuted }}>
                  → {data.total_recipients} Kunden
                </span>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm" style={{ color: COLORS.textMuted }}>
            Kein freigegebener Versand geplant. Gehe zur Monats-Freigabe und approve die Kampagnen.
          </p>
        )}
      </div>

      {/* Last send */}
      <div
        className="p-5 rounded-2xl"
        style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}
      >
        <h3 className="font-black text-sm mb-3 flex items-center gap-2" style={{ color: COLORS.text }}>
          <Envelope size={18} weight="fill" style={{ color: COLORS.orange }} />
          Letzte versendete Mail
        </h3>
        {data.last_send ? (
          <div className="text-sm" style={{ color: COLORS.textMuted }}>
            <div style={{ color: COLORS.text }} className="font-bold">
              {data.last_send.subject}
            </div>
            <div className="mt-1">
              an <strong>{data.last_send.to}</strong> · {fmtDateShort(data.last_send.sent_at)}
            </div>
          </div>
        ) : (
          <p className="text-sm" style={{ color: COLORS.textMuted }}>
            Noch keine Mails versendet.
          </p>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MONTH TAB
// ═══════════════════════════════════════════════════════════
function MonthPanel({ token, initialMonth }) {
  const [month, setMonth] = useState(initialMonth || monthKey(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewId, setPreviewId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async (m) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/newsletter/campaigns?month=${m}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error("Kampagnen konnten nicht geladen werden");
    }
    setLoading(false);
  };

  useEffect(() => {
    load(month);
  }, [month]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(monthKey(d));
  };

  const campaignsByWeek = useMemo(() => {
    if (!data?.campaigns) return {};
    const g = {};
    data.campaigns.forEach((c) => {
      const key = `Woche ${c.week_of_month}`;
      if (!g[key]) g[key] = [];
      g[key].push(c);
    });
    return g;
  }, [data]);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/api/admin/newsletter/campaigns/${id}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(action === "approve" ? "Freigegeben" : "Abgelehnt");
      await load(month);
    } catch (e) {
      toast.error("Aktion fehlgeschlagen");
    }
    setBusyId(null);
  };

  const testSend = async (id) => {
    setBusyId(id);
    try {
      const res = await axios.post(`${API}/api/admin/newsletter/campaigns/${id}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Test-Mail an ${res.data.sent_to} versendet`);
    } catch (e) {
      toast.error("Test-Versand fehlgeschlagen");
    }
    setBusyId(null);
  };

  const bulk = async (action) => {
    if (!window.confirm(action === "approve_all"
      ? `Alle Kampagnen für ${monthLabel(month)} freigeben?`
      : `Alle Kampagnen für ${monthLabel(month)} ablehnen?`)) return;
    try {
      const res = await axios.post(`${API}/api/admin/newsletter/campaigns/bulk`, {
        month, action,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data.updated} Kampagnen aktualisiert`);
      await load(month);
    } catch (e) {
      toast.error("Bulk-Aktion fehlgeschlagen");
    }
  };

  return (
    <div>
      {/* Month selector */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => shiftMonth(-1)}
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}
          >
            <CaretLeft size={18} style={{ color: COLORS.text }} />
          </motion.button>
          <div
            className="px-4 py-2 rounded-xl font-black text-sm"
            style={{ backgroundColor: COLORS.green, color: COLORS.white, minWidth: 160, textAlign: "center" }}
          >
            {monthLabel(month)}
          </div>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => shiftMonth(1)}
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}
          >
            <CaretRight size={18} style={{ color: COLORS.text }} />
          </motion.button>
        </div>

        {data && (
          <div className="flex items-center gap-2 text-sm" style={{ color: COLORS.textMuted }}>
            <Users size={16} weight="fill" />
            <strong style={{ color: COLORS.text }}>{data.recipients}</strong> Kunden
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {data?.campaigns?.some(c => c.status === "draft") && (
        <div
          className="p-3 rounded-xl mb-4 flex items-center justify-between"
          style={{ backgroundColor: "#FEF3C7", border: "1px solid #FDE68A" }}
        >
          <div className="flex items-center gap-2 text-sm" style={{ color: "#92400E" }}>
            <WarningCircle size={18} weight="fill" />
            Einige Kampagnen warten auf Freigabe
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => bulk("approve_all")}
              className="px-3 py-1.5 rounded-lg text-xs font-bold"
              style={{ backgroundColor: COLORS.green, color: COLORS.white }}
            >
              Alle freigeben
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <span className="text-4xl animate-bounce">☕</span>
        </div>
      ) : !data?.campaigns?.length ? (
        <div
          className="p-6 rounded-2xl text-center"
          style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}
        >
          <p style={{ color: COLORS.textMuted }}>Keine Kampagnen für diesen Monat.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(campaignsByWeek).map(([weekLabel, list]) => (
            <div key={weekLabel}>
              <h4 className="text-xs font-black uppercase mb-2" style={{ color: COLORS.textMuted, letterSpacing: 1 }}>
                {weekLabel}
              </h4>
              <div className="space-y-2">
                {list.map((c) => (
                  <CampaignCard
                    key={c.id}
                    c={c}
                    recipients={data.recipients}
                    busy={busyId === c.id}
                    onPreview={() => setPreviewId(c.id)}
                    onApprove={() => act(c.id, "approve")}
                    onReject={() => act(c.id, "reject")}
                    onTest={() => testSend(c.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {previewId && (
          <PreviewModal
            campaignId={previewId}
            token={token}
            onClose={() => setPreviewId(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function CampaignCard({ c, recipients, busy, onPreview, onApprove, onReject, onTest }) {
  const canAct = c.status !== "sent";
  return (
    <motion.div
      layout
      className="p-4 rounded-2xl"
      style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0"
          style={{ backgroundColor: c.color + "20" }}
        >
          {c.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-bold uppercase" style={{ color: COLORS.textMuted, letterSpacing: 1 }}>
              {c.day === "tuesday" ? "Di · Coupon" : "Do · Story"}
            </span>
            <StatusBadge status={c.status} />
          </div>
          <div className="font-bold text-sm mb-1" style={{ color: COLORS.text }}>
            {c.subject}
          </div>
          <div className="text-xs" style={{ color: COLORS.textMuted }}>
            <Clock size={12} weight="fill" className="inline mr-1 mb-0.5" />
            {fmtDate(c.scheduled_for)} · {recipients} Kunden
          </div>
          {c.status === "sent" && c.success_count != null && (
            <div className="text-xs mt-1" style={{ color: COLORS.green }}>
              ✓ {c.success_count} gesendet{c.failure_count ? ` · ${c.failure_count} Fehler` : ""}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <button
          onClick={onPreview}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold"
          style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
        >
          <Eye size={14} weight="fill" /> Vorschau
        </button>
        {canAct && (
          <button
            onClick={onTest}
            disabled={busy}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
          >
            <PaperPlaneTilt size={14} weight="fill" /> Test
          </button>
        )}
        {canAct && c.status !== "approved" && (
          <button
            onClick={onApprove}
            disabled={busy}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{ backgroundColor: COLORS.green, color: COLORS.white }}
          >
            <Check size={14} weight="bold" /> Freigeben
          </button>
        )}
        {canAct && c.status !== "rejected" && (
          <button
            onClick={onReject}
            disabled={busy}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{ backgroundColor: COLORS.red + "15", color: COLORS.red }}
          >
            <X size={14} weight="bold" /> Ablehnen
          </button>
        )}
      </div>
    </motion.div>
  );
}

function PreviewModal({ campaignId, token, onClose }) {
  const src = `${API}/api/admin/newsletter/campaigns/${campaignId}/preview`;
  const [loaded, setLoaded] = useState(false);

  // Browsers won't send Authorization on iframe src; so we fetch HTML via axios
  // and inject via srcdoc.
  const [html, setHtml] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(src, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "text",
        });
        setHtml(res.data);
        setLoaded(true);
      } catch (e) {
        toast.error("Vorschau konnte nicht geladen werden");
        onClose();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl rounded-3xl overflow-hidden flex flex-col"
        style={{ backgroundColor: COLORS.white, maxHeight: "90vh" }}
      >
        <div
          className="flex items-center justify-between p-4"
          style={{ borderBottom: `1px solid ${COLORS.border}` }}
        >
          <h3 className="font-black" style={{ color: COLORS.text }}>Vorschau</h3>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: COLORS.bg }}
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        {!loaded ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <span className="text-4xl animate-bounce">☕</span>
          </div>
        ) : (
          <iframe
            title="Mail-Vorschau"
            srcDoc={html}
            sandbox="allow-same-origin"
            className="flex-1 w-full"
            style={{ border: "none", minHeight: 500 }}
          />
        )}
      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════
// HISTORY TAB
// ═══════════════════════════════════════════════════════════
function HistoryPanel({ token }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (q) params.set("q", q);
      if (type) params.set("type", type);
      const res = await axios.get(`${API}/api/admin/newsletter/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error("Logs konnten nicht geladen werden");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[200px] relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: COLORS.textMuted }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Email oder Betreff suchen…"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm outline-none"
            style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}`, color: COLORS.text }}
          />
        </div>
        <select
          value={type}
          onChange={(e) => { setType(e.target.value); }}
          onBlur={load}
          className="px-3 py-2.5 rounded-xl text-sm outline-none"
          style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}`, color: COLORS.text }}
        >
          <option value="">Alle Typen</option>
          <option value="welcome">Willkommen</option>
          <option value="verification">Verifizierung</option>
          <option value="password_reset">Passwort-Reset</option>
          <option value="order_confirmation">Bestellung</option>
          <option value="coupon_newsletter">Coupon-Newsletter</option>
          <option value="story_newsletter">Story-Newsletter</option>
          <option value="test">Test</option>
        </select>
        <button
          onClick={load}
          className="px-3 py-2.5 rounded-xl text-sm font-bold flex items-center gap-1"
          style={{ backgroundColor: COLORS.green, color: COLORS.white }}
        >
          <ArrowsClockwise size={14} weight="bold" /> Aktualisieren
        </button>
      </div>

      <div className="text-xs mb-3" style={{ color: COLORS.textMuted }}>
        {total} Einträge insgesamt
      </div>

      {loading ? (
        <div className="text-center py-12"><span className="text-4xl animate-bounce">☕</span></div>
      ) : items.length === 0 ? (
        <div
          className="p-6 rounded-2xl text-center"
          style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}
        >
          <p style={{ color: COLORS.textMuted }}>
            Noch keine Mails im Log. Nach dem ersten Versand erscheinen hier alle versendeten Mails.
          </p>
        </div>
      ) : (
        <div
          className="rounded-2xl overflow-hidden"
          style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}
        >
          {items.map((it, idx) => (
            <div
              key={it.id || idx}
              className="p-3 flex items-center gap-3"
              style={idx !== items.length - 1 ? { borderBottom: `1px solid ${COLORS.border}` } : {}}
            >
              {it.status === "sent" ? (
                <CheckCircle size={20} weight="fill" style={{ color: COLORS.green }} />
              ) : (
                <XCircle size={20} weight="fill" style={{ color: COLORS.red }} />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate" style={{ color: COLORS.text }}>
                  {it.subject || "(kein Betreff)"}
                </div>
                <div className="text-xs truncate" style={{ color: COLORS.textMuted }}>
                  {it.to} · {it.type} · {fmtDateShort(it.sent_at)}
                </div>
                {it.error && (
                  <div className="text-xs mt-0.5" style={{ color: COLORS.red }}>
                    ⚠️ {it.error}
                  </div>
                )}
              </div>
              {it.is_test && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-bold"
                  style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}
                >
                  TEST
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// AUTOMATIONS TAB
// ═══════════════════════════════════════════════════════════
function AutomationsPanel({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/newsletter/automations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items);
    } catch (e) {
      toast.error("Automationen konnten nicht geladen werden");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggle = async (a) => {
    setBusyId(a.id);
    try {
      await axios.post(`${API}/api/admin/newsletter/automations/${a.id}/toggle`, {
        enabled: !a.enabled,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(a.enabled ? "Deaktiviert" : "Aktiviert");
      await load();
    } catch (e) {
      toast.error("Umschalten fehlgeschlagen");
    }
    setBusyId(null);
  };

  if (loading) {
    return <div className="text-center py-12"><span className="text-4xl animate-bounce">☕</span></div>;
  }

  return (
    <div className="space-y-3">
      {items.map((a) => (
        <div
          key={a.id}
          className="p-4 rounded-2xl flex items-start justify-between gap-4"
          style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span
                className="text-xs font-bold uppercase px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: a.type === "newsletter" ? "#FEF3C7" : "#DBEAFE",
                  color: a.type === "newsletter" ? "#92400E" : "#1E40AF",
                  letterSpacing: 0.5,
                }}
              >
                {a.type === "newsletter" ? "Newsletter" : "Trigger"}
              </span>
              <span className="text-xs" style={{ color: COLORS.textMuted }}>
                {a.trigger}
              </span>
            </div>
            <div className="font-bold text-sm" style={{ color: COLORS.text }}>
              {a.name}
            </div>
            <div className="text-xs mt-1" style={{ color: COLORS.textMuted }}>
              {a.description}
            </div>
          </div>
          <button
            onClick={() => toggle(a)}
            disabled={busyId === a.id}
            className="relative w-12 h-7 rounded-full transition-colors shrink-0"
            style={{
              backgroundColor: a.enabled ? COLORS.green : "#D1D5DB",
            }}
          >
            <motion.div
              layout
              className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow"
              style={{ left: a.enabled ? 22 : 2 }}
            />
          </button>
        </div>
      ))}
      <div
        className="p-4 rounded-2xl text-xs"
        style={{ backgroundColor: COLORS.bg, color: COLORS.textMuted, border: `1px dashed ${COLORS.border}` }}
      >
        💡 Newsletter-Kampagnen müssen pro Monat im Tab „Monats-Freigabe" einzeln bestätigt werden.
        Trigger-Mails laufen automatisch, solange sie aktiviert sind.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN TAB
// ═══════════════════════════════════════════════════════════
export default function NewsletterTab({ token }) {
  const [active, setActive] = useState("overview");
  const [initialMonth, setInitialMonth] = useState(null);

  const onJumpToMonth = (m) => {
    setInitialMonth(m);
    setActive("month");
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-2xl font-black mb-1" style={{ color: COLORS.text }}>
          Newsletter & Automationen
        </h2>
        <p className="text-sm" style={{ color: COLORS.textMuted }}>
          Prüfe und gib Mails frei, bevor sie automatisch an alle Kunden versendet werden.
        </p>
      </div>

      <SubTabBar active={active} onChange={setActive} />

      {active === "overview" && <OverviewPanel token={token} onJumpToMonth={onJumpToMonth} />}
      {active === "month" && <MonthPanel token={token} initialMonth={initialMonth} />}
      {active === "history" && <HistoryPanel token={token} />}
      {active === "automations" && <AutomationsPanel token={token} />}
    </div>
  );
}
